/**
 * Stamina bar + power meter for one side (HUD Kit D). P1 sits top-left, P2 top-right (mirrored).
 *
 * Stamina: 8 segments (12.5 each). Below the cheapest feint (`HudSnapshot.mustBeReal`) the bar turns red and the
 * MUST BE REAL tag shows for BOTH players — the read is public on purpose. A refused feint shakes the bar.
 * Power: liquid fill with quarter ticks and the signature special icon; at 100 it glows and shows
 * "<SPECIAL> READY!" with the Special key; while a special telegraphs/runs it shows its name (and seconds).
 */
import { useEffect } from 'preact/hooks';
import { specialInfo, STAMINA, POWER } from '@hf/shared';
import { SpecialPhase, type HudSnapshot } from '@hf/sim';
import { useServices } from '../actions';
import { SpecialIcon } from '../components';
import { abbrOf, characterIdAt, slotTag } from '@hf/client-core/ui-model/format';
import type { MatchSetup } from '@hf/client-core/ui-model/types';
import { ButtonCap, promptDevice, usePulse } from './common';
import {
  STAMINA_SEGMENTS,
  staminaSegments,
  deniedText,
  specialReadyVisible,
} from '@hf/client-core/ui-model/hud';
export { STAMINA_SEGMENTS, staminaSegments, deniedText } from '@hf/client-core/ui-model/hud';

export function Meters({
  h,
  side,
  setup,
  specials,
}: {
  h: HudSnapshot;
  side: 0 | 1;
  setup: MatchSetup | null;
  specials: boolean;
}) {
  const { input, audio } = useServices();
  const denied = usePulse('stamina_denied', 600, side);
  const noSpecial = usePulse('power_denied', 700, side);
  useEffect(() => {
    if (!noSpecial) return;
    try {
      audio.sfx('stamina_denied'); // the same "nuh-uh" as a refused feint
    } catch {
      /* audio optional */
    }
  }, [noSpecial?.id]);
  // an online opponent's charged special is not ours to announce (his meter still fills; firing it shows for all)
  const mine = specialReadyVisible(setup, side);
  const fullPulse = usePulse('power_full', 1400, side);
  const full = mine ? fullPulse : null;
  const stamina = h.stamina[side];
  const power = Math.max(0, Math.min(POWER.max, h.power[side]));
  const real = h.mustBeReal[side];
  const lit = staminaSegments(stamina);
  const sp = h.special[side];
  const info = specialInfo(sp.id as never);
  const hasSpecial = sp.id !== 0;
  const ready = hasSpecial && sp.ready && mine;
  const busy = sp.phase === SpecialPhase.TELEGRAPH || sp.phase === SpecialPhase.ACTIVE;
  const char = characterIdAt(h.characters[side]);
  const human = setup?.slots[side].kind === 'human';
  const device = human ? promptDevice(setup, side, input.lastActiveDevice()) : null;
  const tag = slotTag(setup, side);
  const offense = h.offense === side;

  return (
    <div class={`hf-meters ${side === 0 ? 'home' : 'away'}`} aria-label={`${tag} meters`}>
      <div class={`hf-stamina ${real ? 'real' : ''} ${denied ? 'denied' : ''}`} key={denied?.id ?? 'stamina'}>
        <span class="hf-stamina__who t-broadcast">
          {abbrOf(char)}
          <small>{tag}</small>
          {offense ? <i class="hf-stamina__ball" aria-label="has the ball" /> : null}
        </span>
        <span class="hf-stamina__bolt" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M13 2 4 14h7l-1 8 9-12h-7z" fill="currentColor" />
          </svg>
        </span>
        <span
          class="hf-stamina__segs"
          role="meter"
          aria-label="Stamina"
          aria-valuenow={Math.round(stamina)}
          aria-valuemin={0}
          aria-valuemax={STAMINA.max}
        >
          {Array.from({ length: STAMINA_SEGMENTS }, (_, i) => (
            <span
              key={i}
              class={`hf-seg ${i < lit ? 'on' : ''} ${i === lit - 1 && lit < STAMINA_SEGMENTS ? 'edge' : ''}`}
            />
          ))}
        </span>
      </div>
      {real ? (
        <div class="hf-mustbereal t-display" role="status">
          MUST BE REAL
        </div>
      ) : null}
      {specials ? (
        <div
          class={`hf-power ${ready ? 'full' : ''} ${full ? 'flash' : ''} ${!hasSpecial ? 'none' : ''} ${noSpecial ? 'denied' : ''}`}
          key={noSpecial?.id ?? 'power'}
        >
          <span class="hf-power__label t-display">POWER</span>
          <span
            class="hf-power__track"
            role="meter"
            aria-label="Power"
            aria-valuenow={Math.round(power)}
            aria-valuemin={0}
            aria-valuemax={POWER.max}
          >
            <span class="hf-power__fill" style={{ width: `${power}%` }} />
            <span class="hf-power__ticks" aria-hidden="true" />
          </span>
          <span class="hf-power__icon" title={info.name}>
            {hasSpecial ? <SpecialIcon special={sp.id} /> : null}
          </span>
          {noSpecial && !busy ? (
            <span class="hf-power__nope t-broadcast" role="status">
              {deniedText(power, ready)}
            </span>
          ) : null}
        </div>
      ) : null}
      {specials && (ready || busy) ? (
        <div class={`hf-ready ${busy ? 'busy' : ''}`} key={busy ? 'busy' : 'ready'}>
          <span class="t-display">
            {busy
              ? `${info.name}!${sp.phase === SpecialPhase.ACTIVE && sp.timerSec > 0 ? ` ${sp.timerSec}s` : ''}`
              : `${info.name} READY!`}
          </span>
          {!busy && device ? (
            <span class="hf-ready__key">
              <ButtonCap device={device} button="S" />
            </span>
          ) : !busy ? (
            <span class="hf-ready__key t-broadcast">SPECIAL</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
